import Version, { WIDGET_TYPE, WIDGET_TYPES } from 'embed_v2/types/version'
import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import { serialize } from 'core/helpers'
import Secrets from 'Secrets'

type TRACK_EVENT_NAME = 'impression' | 'click' | 'visit'

type TrackPayload = {
  wid: number
  aid: number
  wiid: number
  wivid: number
  wsid?: number
  source?: string
}

type ImpressionTrackPayload = {
  widget_type: WIDGET_TYPE
} & TrackEvent

class Visitor {
  cookiesEnabled: boolean
  visitedVersions?: number[]
  _visitedWidgets?: number[]
  _token?: string

  constructor() {
    this.cookiesEnabled = this.isCookieEnabled()
  }

  ensureSetToken() {
    if (!this.token) {
      this.token = uuidv4()
    }
  }

  trackVisit(version: Version, source: string | undefined, webinarSession = version.formatted_sessions[0]) {
    if (!webinarSession && version.widget_type !== WIDGET_TYPES.THANK_YOU) return

    const payload: TrackPayload = {
      wid: version.webinar.id,
      aid: version.webinar.account_id,
      wiid: version.widget_id,
      wivid: version.id,
      ...(webinarSession && { wsid: webinarSession.id }),
      ...(source?.trim().length && { source })
    }

    this.track('impression', { ...payload, widget_type: version.widget_type })
    const visitedVersions = this.visitedVersions || []
    if (!visitedVersions.includes(version.id)) {
      this.track('visit', payload)
      visitedVersions.push(version.id)
      this.visitedVersions = visitedVersions
    }
  }

  track(eventName: TRACK_EVENT_NAME, params: TrackPayload | ImpressionTrackPayload, callback?: () => void) {
    if (window.WF_PLATFORM) return

    const payload = {
      ...params,
      type: eventName,
      token: this.token
    }

    const e = new Image(1, 1)
    e.src = `${this.trackHost}/embed/v2/viewers/track?${serialize(payload)}`
    e.style.display = 'none'
    if (typeof callback === 'function') {
      let eventTracked = false
      setTimeout(() => {
        if (!eventTracked) {
          callback()
        }
      }, 1000)

      e.addEventListener('load', () => {
        eventTracked = true
        callback()
      })
    }
  }

  get token(): string {
    return this.cookiesEnabled ? Cookies.get('_webby_visitor_token')! : this._token!
  }

  set token(value: string) {
    if (this.cookiesEnabled) {
      Cookies.set('_webby_visitor_token', value, { expires: 30 })
    } else {
      this._token = value
    }
  }

  get visitedWidgets() {
    if (this.cookiesEnabled) {
      const value = Cookies.get('_webby_visitor_visitedWidgets')
      if (value) {
        return JSON.parse(value)
      }
    }

    return this._visitedWidgets
  }

  set visitedWidgets(value) {
    this.cookiesEnabled
      ? Cookies.set('_webby_visitor_visitedWidgets', value, { expires: 30 })
      : (this._visitedWidgets = value)
  }

  get trackHost() {
    return Secrets.meetingHost
  }

  get fbp() {
    return this.cookiesEnabled ? Cookies.get('_fbp') : null
  }

  get fbc() {
    return this.cookiesEnabled ? Cookies.get('_fbc') : null
  }

  findCookie(name: string) {
    try {
      return Cookies.get(name)
    } catch {
      return null
    }
  }

  isCookieEnabled() {
    try {
      const name = `dummy-${Math.random()}`
      Cookies.set(name, '1', { expires: 1 })
      return !!Cookies.get(name)
    } catch {
      return false
    }
  }
}

export default new Visitor()
